/**
 * 用途：Google 搜索广告创建向导（分步骤），由 CreateAdWizard 根据平台参数调用
 * 路由：无（子组件，通过 /campaigns/google/create 间接访问）
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { accountsApi, type CreateAdFullRequest, type DraftPayloadRequest } from '../api/accounts';
import { useAppStore } from '../store/useAppStore';
import { confirmDialog } from '../store/useConfirmStore';
import { MultiSelect } from '../components/MultiSelect';
import {
  DateTimePicker,
  FormGroup as SharedFormGroup,
  Hint as SharedHint,
  HorizontalStepBar,
  Label as SharedLabel,
  SectionCard as SharedSectionCard,
  UploadZone,
  WizardErrorBanner,
} from './adWizardShared';

type WizardStep = 1 | 2 | 3;
type KeywordMatchType = 'BROAD' | 'PHRASE' | 'EXACT';

type KeywordItem = {
  text: string;
  matchType: KeywordMatchType;
};

type GoogleImageAsset = {
  name: string;
  data_url: string;
};

type GoogleSitelink = {
  linkText: string;
  finalURL: string;
  description1: string;
  description2: string;
};

type GoogleStructuredSnippet = {
  header: string;
  valuesText: string;
};

type GooglePromotion = {
  occasion: string;
  discountModifier: string;
  percentOff: string;
  moneyAmountOff: string;
  currencyCode: string;
  promotionCode: string;
  ordersOverAmount: string;
  promotionTarget: string;
  finalURL: string;
  startDate: string;
  endDate: string;
  languageCode: string;
};

type GooglePriceItem = {
  header: string;
  description: string;
  price: string;
  currencyCode: string;
  unit: string;
  finalURL: string;
};

type GooglePriceAsset = {
  priceType: string;
  languageCode: string;
  items: GooglePriceItem[];
};

type QuickOption = {
  value: string;
  label: string;
};

type AccountOption = {
  id: string;
  account_id: string;
  account_name?: string;
};

type GoogleAdWizardProps = {
  brandId: string;
  siteId: string;
  onCancel: () => void;
  onCreated: () => void;
};

type ValidationIssue = {
  message: string;
  field?: string;
};

type GoogleCreateDraftPayload = Record<string, unknown>;

const GOOGLE_OBJECTIVES = [
  { value: 'SALES', label: '销售' },
  { value: 'LEADS', label: '潜在客户' },
  { value: 'TRAFFIC', label: '网站流量' },
  { value: 'AWARENESS', label: '品牌认知' },
];

const COMMON_COUNTRIES: QuickOption[] = [
  { value: 'US', label: '美国 (US)' },
  { value: 'CA', label: '加拿大 (CA)' },
  { value: 'GB', label: '英国 (GB)' },
  { value: 'AU', label: '澳大利亚 (AU)' },
  { value: 'DE', label: '德国 (DE)' },
  { value: 'FR', label: '法国 (FR)' },
  { value: 'JP', label: '日本 (JP)' },
  { value: 'SG', label: '新加坡 (SG)' },
];
const COMMON_LANGUAGES: QuickOption[] = [
  { value: 'en', label: '英语 (en)' },
  { value: 'es', label: '西班牙语 (es)' },
  { value: 'fr', label: '法语 (fr)' },
  { value: 'de', label: '德语 (de)' },
  { value: 'ja', label: '日语 (ja)' },
  { value: 'zh-CN', label: '简体中文 (zh-CN)' },
  { value: 'zh-TW', label: '繁体中文 (zh-TW)' },
];
const MATCH_TYPES: KeywordMatchType[] = ['BROAD', 'PHRASE', 'EXACT'];
const MATCH_TYPE_LABELS: Record<KeywordMatchType, string> = {
  BROAD: '广泛匹配',
  PHRASE: '词组匹配',
  EXACT: '完全匹配',
};

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return <SharedSectionCard title={title}>{children}</SharedSectionCard>;
}

function FormGroup({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <SharedFormGroup style={style}>{children}</SharedFormGroup>;
}

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return <SharedLabel required={required}>{children}</SharedLabel>;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <SharedHint>{children}</SharedHint>;
}

function StepBar({ step }: { step: WizardStep }) {
  return <HorizontalStepBar step={step} />;
}

function ToggleChips({
  options,
  selected,
  onToggle,
}: {
  options: QuickOption[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
      {options.map((option) => {
        const active = selected.includes(option.value);
        return (
          <label
            key={option.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: 8,
              background: active ? 'var(--primary-bg)' : 'var(--surface2)',
              color: active ? 'var(--primary)' : 'var(--text2)',
              padding: '6px 10px',
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => onToggle(option.value)}
              style={{ accentColor: 'var(--primary)' }}
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

function emptyKeyword(): KeywordItem {
  return { text: '', matchType: 'PHRASE' };
}

function emptySitelink(): GoogleSitelink {
  return { linkText: '', finalURL: '', description1: '', description2: '' };
}

function emptySnippet(): GoogleStructuredSnippet {
  return { header: '', valuesText: '' };
}

function emptyPromotion(): GooglePromotion {
  return { occasion: '', discountModifier: 'NONE', percentOff: '', moneyAmountOff: '', currencyCode: 'USD', promotionCode: '', ordersOverAmount: '', promotionTarget: '', finalURL: '', startDate: '', endDate: '', languageCode: 'en' };
}

function emptyPriceItem(): GooglePriceItem {
  return { header: '', description: '', price: '', currencyCode: 'USD', unit: 'NONE', finalURL: '' };
}

function emptyPriceAsset(): GooglePriceAsset {
  return { priceType: 'PRODUCT_CATEGORIES', languageCode: 'en', items: [emptyPriceItem(), emptyPriceItem(), emptyPriceItem()] };
}

const PROMOTION_OCCASIONS: QuickOption[] = [
  { value: '', label: '无特定时机' },
  { value: 'NEW_YEARS', label: '新年' },
  { value: 'VALENTINES_DAY', label: '情人节' },
  { value: 'EASTER', label: '复活节' },
  { value: 'MOTHERS_DAY', label: '母亲节' },
  { value: 'FATHERS_DAY', label: '父亲节' },
  { value: 'LABOR_DAY', label: '劳动节' },
  { value: 'BACK_TO_SCHOOL', label: '返校季' },
  { value: 'HALLOWEEN', label: '万圣节' },
  { value: 'BLACK_FRIDAY', label: '黑色星期五' },
  { value: 'CYBER_MONDAY', label: '网络星期一' },
  { value: 'CHRISTMAS', label: '圣诞节' },
  { value: 'SINGLES_DAY', label: '双十一' },
  { value: 'YEAR_END_GIFT', label: '年末促销' },
];

const PRICE_TYPES: QuickOption[] = [
  { value: 'PRODUCT_CATEGORIES', label: '产品类目' },
  { value: 'BRANDS', label: '品牌' },
  { value: 'EVENTS', label: '活动' },
  { value: 'LOCATIONS', label: '地点' },
  { value: 'NEIGHBORHOODS', label: '区域' },
  { value: 'SERVICE_CATEGORIES', label: '服务类目' },
  { value: 'SERVICE_TIERS', label: '服务等级' },
];

const PRICE_UNITS: QuickOption[] = [
  { value: 'NONE', label: '不显示' },
  { value: 'PER_HOUR', label: '/小时' },
  { value: 'PER_DAY', label: '/天' },
  { value: 'PER_WEEK', label: '/周' },
  { value: 'PER_MONTH', label: '/月' },
  { value: 'PER_YEAR', label: '/年' },
];

function parseListInput(value: string): string[] {
  return value
    .split(/[\n,;]+/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseCodeList(value: string): string[] {
  return Array.from(new Set(parseListInput(value).map((item) => item.toUpperCase())));
}

function parseLanguageCodeList(value: string): string[] {
  return Array.from(new Set(parseListInput(value).map((item) => item)));
}



function compactTextList(list: string[]): string[] {
  return list.map((item) => item.trim()).filter(Boolean);
}

function toggleDelimitedValue(raw: string, value: string, uppercase = true): string {
  const normalizedValue = uppercase ? value.toUpperCase() : value;
  const list = uppercase ? parseCodeList(raw) : parseLanguageCodeList(raw);
  const next = list.includes(normalizedValue)
    ? list.filter((item) => item !== normalizedValue)
    : [...list, normalizedValue];
  return next.join(', ');
}

function toPositiveNumber(value: string): number | undefined {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

function isValidURL(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('读取文件失败'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
}

function toGoogleRFC3339(value: string): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, '0');
    const minutes = String(abs % 60).padStart(2, '0');
    return `${value}:00${sign}${hours}:${minutes}`;
  }
  return value;
}

export function CreateGoogleSearchAdWizard({ brandId, siteId, onCancel, onCreated }: GoogleAdWizardProps) {
  const { hasPermission } = useAppStore();
  const [step, setStep] = useState<WizardStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const formCardRef = useRef<HTMLDivElement>(null);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [selectedAccountID, setSelectedAccountID] = useState('');

  const [campaignName, setCampaignName] = useState('');
  const [campaignObjective, setCampaignObjective] = useState('SALES');
  const [campaignStatus, setCampaignStatus] = useState('PAUSED');
  const [dailyBudget, setDailyBudget] = useState('20');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [countryCodesText, setCountryCodesText] = useState('US');
  const [languageCodesText, setLanguageCodesText] = useState('en');
  const [selectedLocations, setSelectedLocations] = useState<{ id: string; name: string }[]>([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationResults, setLocationResults] = useState<{ id: string; name: string; canonical_name: string }[]>([]);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<{ id: string; name: string }[]>([]);
  const [languageSearch, setLanguageSearch] = useState('');
  const [languageResults, setLanguageResults] = useState<{ id: string; name: string }[]>([]);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [enableGoogleSearch, setEnableGoogleSearch] = useState(true);
  const [enableSearchNetwork, setEnableSearchNetwork] = useState(true);
  const [enablePartners, setEnablePartners] = useState(false);
  const [enableDisplay, setEnableDisplay] = useState(false);

  const [adsetName, setAdsetName] = useState('');
  const [adsetStatus, setAdsetStatus] = useState('PAUSED');
  const [cpcBid, setCpcBid] = useState('');
  const [keywords, setKeywords] = useState<KeywordItem[]>([emptyKeyword()]);

  const [adName, setAdName] = useState('');
  const [adStatus, setAdStatus] = useState('PAUSED');
  const [finalURL, setFinalURL] = useState('');
  const [path1, setPath1] = useState('');
  const [path2, setPath2] = useState('');
  const [headlines, setHeadlines] = useState<string[]>(['', '', '']);
  const [descriptions, setDescriptions] = useState<string[]>(['', '']);
  const [businessName, setBusinessName] = useState('');
  const [businessLogo, setBusinessLogo] = useState<GoogleImageAsset | null>(null);
  const [marketingImages, setMarketingImages] = useState<GoogleImageAsset[]>([]);
  const [callouts, setCallouts] = useState<string[]>(['']);
  const [sitelinks, setSitelinks] = useState<GoogleSitelink[]>([emptySitelink()]);
  const [structuredSnippets, setStructuredSnippets] = useState<GoogleStructuredSnippet[]>([emptySnippet()]);
  const [snippetHeaderOptions, setSnippetHeaderOptions] = useState<{ value: string; label: string }[]>([]);
  const [callCountryCode, setCallCountryCode] = useState('');
  const [callPhoneNumber, setCallPhoneNumber] = useState('');
  const [promotions, setPromotions] = useState<GooglePromotion[]>([]);
  const [priceAssets, setPriceAssets] = useState<GooglePriceAsset[]>([]);
  const draftKey = 'google:create:search';
  const initialDraftSnapshotRef = useRef<GoogleCreateDraftPayload | null>(null);
  const draftLoadedRef = useRef(true);

  useEffect(() => {
    if (!brandId) return;
    accountsApi.list({ brand_id: brandId, site_id: siteId, platform: 'google', scope: 'campaign_write', page: 1, size: 100 })
      .then((response) => {
        const options = Array.isArray(response.data)
          ? response.data.map((item) => ({
              id: item.id,
              account_id: item.account_id,
              account_name: item.account_name,
            }))
          : [];
        setAccountOptions(options);
        setSelectedAccountID((current) => current || options[0]?.account_id || '');
      })
      .catch(() => {
        setAccountOptions([]);
        setSelectedAccountID('');
      });
  }, [brandId, siteId]);

  useEffect(() => {
    accountsApi.getGoogleSnippetHeaders()
      .then((r) => setSnippetHeaderOptions(r.list ?? []))
      .catch(() => setSnippetHeaderOptions([]));
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  useEffect(() => {
    if (!locationSearch.trim() || !selectedAccountID) { setLocationResults([]); return; }
    const t = setTimeout(() => {
      accountsApi.searchGoogleGeoTargets({ brand_id: brandId, account_id: selectedAccountID, keyword: locationSearch.trim() })
        .then((r) => { setLocationResults((r.list ?? []).map((i) => ({ id: i.id, name: i.name, canonical_name: i.canonical_name }))); setLocationDropdownOpen(true); })
        .catch(() => setLocationResults([]));
    }, 400);
    return () => clearTimeout(t);
  }, [locationSearch, selectedAccountID, brandId]);

  useEffect(() => {
    if (!languageSearch.trim() || !selectedAccountID) { setLanguageResults([]); return; }
    const t = setTimeout(() => {
      accountsApi.searchGoogleLanguages({ brand_id: brandId, account_id: selectedAccountID, keyword: languageSearch.trim() })
        .then((r) => { setLanguageResults((r.list ?? []).map((i) => ({ id: i.id, name: i.name }))); setLanguageDropdownOpen(true); })
        .catch(() => setLanguageResults([]));
    }, 400);
    return () => clearTimeout(t);
  }, [languageSearch, selectedAccountID, brandId]);

  const setFieldRef = (key: string) => (node: HTMLDivElement | null) => {
    fieldRefs.current[key] = node;
  };

  const scrollFormToTop = () => {
    if (formCardRef.current) {
      formCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToField = (key?: string) => {
    if (!key) return;
    const target = fieldRefs.current[key];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const keywordCount = useMemo(
    () => keywords.map((item) => item.text.trim()).filter(Boolean).length,
    [keywords],
  );

  const payload = useMemo<CreateAdFullRequest>(() => {
    const normalizedHeadlines = compactTextList(headlines);
    const normalizedDescriptions = compactTextList(descriptions);
    const normalizedCallouts = compactTextList(callouts);
    const normalizedSitelinks = sitelinks
      .map((item) => ({
        link_text: item.linkText.trim(),
        final_url: item.finalURL.trim(),
        description1: item.description1.trim(),
        description2: item.description2.trim(),
      }))
      .filter((item) => item.link_text && item.final_url);
    const normalizedSnippets = structuredSnippets
      .map((item) => ({
        header: item.header.trim(),
        values: parseListInput(item.valuesText),
      }))
      .filter((item) => item.header && item.values.length > 0);

    return {
      platform: 'google',
      account_id: selectedAccountID,
      campaign_name: campaignName.trim(),
      campaign_objective: campaignObjective,
      campaign_status: campaignStatus,
      daily_budget: Number(dailyBudget),
      start_time: startDate || undefined,
      end_time: endDate || undefined,
      country_codes: parseCodeList(countryCodesText),
      language_codes: parseLanguageCodeList(languageCodesText),
      location_ids: selectedLocations.map((l) => l.id),
      language_ids: selectedLanguages.map((l) => l.id),
      enable_google_search: enableGoogleSearch,
      enable_search_network: enableSearchNetwork,
      enable_partner_search_network: enablePartners,
      enable_display_network: enableDisplay,
      adset_name: adsetName.trim(),
      adset_status: adsetStatus,
      cpc_bid: toPositiveNumber(cpcBid),
      keywords: keywords
        .map((item) => ({ text: item.text.trim(), match_type: item.matchType }))
        .filter((item) => item.text),
      ad_name: adName.trim(),
      ad_status: adStatus,
      link: finalURL.trim(),
      landing_url: finalURL.trim(),
      path1: path1.trim() || undefined,
      path2: path2.trim() || undefined,
      headline: normalizedHeadlines[0] || undefined,
      headlines: normalizedHeadlines,
      description: normalizedDescriptions[0] || undefined,
      descriptions: normalizedDescriptions,
      business_name: businessName.trim() || undefined,
      business_logo: businessLogo ?? undefined,
      marketing_images: marketingImages,
      sitelinks: normalizedSitelinks,
      callouts: normalizedCallouts,
      structured_snippets: normalizedSnippets,
      call_asset: callCountryCode.trim() && callPhoneNumber.trim()
        ? {
            country_code: callCountryCode.trim().toUpperCase(),
            phone_number: callPhoneNumber.trim(),
          }
        : undefined,
      promotion_assets: promotions
        .filter((p) => p.finalURL.trim() && p.promotionTarget.trim())
        .map((p) => ({
          promotion_target: p.promotionTarget.trim(),
          occasion: p.occasion || undefined,
          discount_modifier: p.discountModifier || undefined,
          percent_off: toPositiveNumber(p.percentOff) ?? 0,
          money_amount_off: toPositiveNumber(p.moneyAmountOff) ?? 0,
          currency_code: p.currencyCode || 'USD',
          promotion_code: p.promotionCode.trim() || undefined,
          orders_over_amount: toPositiveNumber(p.ordersOverAmount) ?? 0,
          final_url: p.finalURL.trim(),
          start_date: p.startDate || undefined,
          end_date: p.endDate || undefined,
          language_code: p.languageCode || 'en',
        })),
      price_assets: priceAssets
        .filter((pa) => pa.priceType && pa.items.some((i) => i.header.trim() && i.finalURL.trim()))
        .map((pa) => ({
          price_type: pa.priceType,
          language_code: pa.languageCode || 'en',
          price_items: pa.items
            .filter((i) => i.header.trim() && i.finalURL.trim())
            .map((i) => ({
              header: i.header.trim(),
              description: i.description.trim() || undefined,
              price: Number(i.price) || 0,
              currency_code: i.currencyCode || 'USD',
              unit: i.unit || 'NONE',
              final_url: i.finalURL.trim(),
            })),
        })),
      site_id: siteId || undefined,
    };
  }, [
    adName,
    adStatus,
    adsetName,
    adsetStatus,
    businessLogo,
    businessName,
    callCountryCode,
    callPhoneNumber,
    callouts,
    campaignName,
    campaignObjective,
    campaignStatus,
    countryCodesText,
    cpcBid,
    dailyBudget,
    descriptions,
    enableDisplay,
    enableGoogleSearch,
    enablePartners,
    enableSearchNetwork,
    endDate,
    finalURL,
    headlines,
    keywords,
    languageCodesText,
    selectedLanguages,
    selectedLocations,
    marketingImages,
    path1,
    path2,
    priceAssets,
    promotions,
    selectedAccountID,
    siteId,
    sitelinks,
    startDate,
    structuredSnippets,
  ]);

  const buildDraftPayload = useCallback((): GoogleCreateDraftPayload => ({
    step,
    selectedAccountID,
    campaignName,
    campaignObjective,
    campaignStatus,
    dailyBudget,
    startDate,
    endDate,
    countryCodesText,
    languageCodesText,
    selectedLocations,
    selectedLanguages,
    enableGoogleSearch,
    enableSearchNetwork,
    enablePartners,
    enableDisplay,
    adsetName,
    adsetStatus,
    cpcBid,
    keywords,
    adName,
    adStatus,
    finalURL,
    path1,
    path2,
    headlines,
    descriptions,
    businessName,
    businessLogo,
    marketingImages,
    callouts,
    sitelinks,
    structuredSnippets,
    callCountryCode,
    callPhoneNumber,
    promotions,
    priceAssets,
  }), [
    step, selectedAccountID, campaignName, campaignObjective, campaignStatus, dailyBudget, startDate, endDate,
    countryCodesText, languageCodesText, selectedLocations, selectedLanguages, enableGoogleSearch, enableSearchNetwork,
    enablePartners, enableDisplay, adsetName, adsetStatus, cpcBid, keywords, adName, adStatus, finalURL, path1, path2,
    headlines, descriptions, businessName, businessLogo, marketingImages, callouts, sitelinks, structuredSnippets,
    callCountryCode, callPhoneNumber, promotions, priceAssets,
  ]);

  const applyDraftPayload = useCallback((payload: GoogleCreateDraftPayload) => {
    if (payload.step === 1 || payload.step === 2 || payload.step === 3) setStep(payload.step);
    if (typeof payload.selectedAccountID === 'string') setSelectedAccountID(payload.selectedAccountID);
    if (typeof payload.campaignName === 'string') setCampaignName(payload.campaignName);
    if (typeof payload.campaignObjective === 'string') setCampaignObjective(payload.campaignObjective);
    if (typeof payload.campaignStatus === 'string') setCampaignStatus(payload.campaignStatus);
    if (typeof payload.dailyBudget === 'string') setDailyBudget(payload.dailyBudget);
    if (typeof payload.startDate === 'string') setStartDate(payload.startDate);
    if (typeof payload.endDate === 'string') setEndDate(payload.endDate);
    if (typeof payload.countryCodesText === 'string') setCountryCodesText(payload.countryCodesText);
    if (typeof payload.languageCodesText === 'string') setLanguageCodesText(payload.languageCodesText);
    if (Array.isArray(payload.selectedLocations)) setSelectedLocations(payload.selectedLocations as { id: string; name: string }[]);
    if (Array.isArray(payload.selectedLanguages)) setSelectedLanguages(payload.selectedLanguages as { id: string; name: string }[]);
    if (typeof payload.enableGoogleSearch === 'boolean') setEnableGoogleSearch(payload.enableGoogleSearch);
    if (typeof payload.enableSearchNetwork === 'boolean') setEnableSearchNetwork(payload.enableSearchNetwork);
    if (typeof payload.enablePartners === 'boolean') setEnablePartners(payload.enablePartners);
    if (typeof payload.enableDisplay === 'boolean') setEnableDisplay(payload.enableDisplay);
    if (typeof payload.adsetName === 'string') setAdsetName(payload.adsetName);
    if (typeof payload.adsetStatus === 'string') setAdsetStatus(payload.adsetStatus);
    if (typeof payload.cpcBid === 'string') setCpcBid(payload.cpcBid);
    if (Array.isArray(payload.keywords)) setKeywords(payload.keywords as KeywordItem[]);
    if (typeof payload.adName === 'string') setAdName(payload.adName);
    if (typeof payload.adStatus === 'string') setAdStatus(payload.adStatus);
    if (typeof payload.finalURL === 'string') setFinalURL(payload.finalURL);
    if (typeof payload.path1 === 'string') setPath1(payload.path1);
    if (typeof payload.path2 === 'string') setPath2(payload.path2);
    if (Array.isArray(payload.headlines)) setHeadlines(payload.headlines as string[]);
    if (Array.isArray(payload.descriptions)) setDescriptions(payload.descriptions as string[]);
    if (typeof payload.businessName === 'string') setBusinessName(payload.businessName);
    if (payload.businessLogo && typeof payload.businessLogo === 'object') setBusinessLogo(payload.businessLogo as GoogleImageAsset);
    if (Array.isArray(payload.marketingImages)) setMarketingImages(payload.marketingImages as GoogleImageAsset[]);
    if (Array.isArray(payload.callouts)) setCallouts(payload.callouts as string[]);
    if (Array.isArray(payload.sitelinks)) setSitelinks(payload.sitelinks as GoogleSitelink[]);
    if (Array.isArray(payload.structuredSnippets)) setStructuredSnippets(payload.structuredSnippets as GoogleStructuredSnippet[]);
    if (typeof payload.callCountryCode === 'string') setCallCountryCode(payload.callCountryCode);
    if (typeof payload.callPhoneNumber === 'string') setCallPhoneNumber(payload.callPhoneNumber);
    if (Array.isArray(payload.promotions)) setPromotions(payload.promotions as GooglePromotion[]);
    if (Array.isArray(payload.priceAssets)) setPriceAssets(payload.priceAssets as GooglePriceAsset[]);
  }, []);

  const buildDraftRequest = useCallback((payload: GoogleCreateDraftPayload): DraftPayloadRequest => ({
    platform: 'google',
    scene: 'create',
    entity_type: step === 1 ? 'campaign' : step === 2 ? 'adset' : 'ad',
    draft_key: draftKey,
    draft_name: 'google-search-create',
    site_id: siteId || undefined,
    payload,
  }), [draftKey, siteId, step]);

  const saveManualDraftSilently = useCallback(async (payloadOverride?: GoogleCreateDraftPayload) => {
    if (!brandId || !draftLoadedRef.current) return;
    const payload = payloadOverride ?? buildDraftPayload();
    try {
      await accountsApi.saveManualDraft(brandId, buildDraftRequest(payload));
    } catch {
      // ignore background save errors
    }
  }, [brandId, buildDraftPayload, buildDraftRequest]);

  const handleCancelWithDraft = useCallback(async () => {
    await saveManualDraftSilently();
    onCancel();
  }, [onCancel, saveManualDraftSilently]);

  const handleResetDraft = useCallback(async () => {
    if (!initialDraftSnapshotRef.current) return;
    const ok = await confirmDialog({
      title: '重置内容',
      message: '将回到初始创建内容，当前未保存的修改会丢失，是否继续？',
      confirmText: '重置',
      danger: true,
    });
    if (!ok) return;
    applyDraftPayload(initialDraftSnapshotRef.current);
    setError(null);
  }, [applyDraftPayload]);

  useEffect(() => {
    initialDraftSnapshotRef.current = buildDraftPayload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateStep = (): ValidationIssue | null => {
    if (step === 1) {
      if (!selectedAccountID) return { message: '请选择 Google 广告账户', field: 'account' };
      if (!campaignName.trim()) return { message: '广告系列名称不能为空', field: 'campaignName' };
      if (!toPositiveNumber(dailyBudget)) return { message: '日预算必须大于 0', field: 'dailyBudget' };
      if (!enableGoogleSearch && !enableSearchNetwork && !enablePartners && !enableDisplay) {
        return { message: '至少启用一个 Google 广告网络', field: 'networks' };
      }
      const countryTokens = parseListInput(countryCodesText);
      if (countryTokens.some((item) => !/^[A-Za-z]{2}$/.test(item))) {
        return { message: '国家代码必须使用 2 位 ISO 编码，例如 US、GB', field: 'countryCodes' };
      }
      const languageTokens = parseListInput(languageCodesText);
      if (languageTokens.some((item) => !/^[A-Za-z]{2,3}(-[A-Za-z]{2,4})?$/.test(item))) {
        return { message: '语言代码必须使用 Google 语言编码，例如 en、zh-CN', field: 'languageCodes' };
      }
    }

    if (step === 2) {
      if (!adsetName.trim()) return { message: '广告组名称不能为空', field: 'adsetName' };
      const validKeywords = keywords.filter((item) => item.text.trim());
      if (validKeywords.length === 0) return { message: '至少添加一个关键词', field: 'keywords' };
      if (validKeywords.length > 50) return { message: '当前 Google 搜索广告表单每个广告组最多支持 50 个关键词', field: 'keywords' };
      const invalidKeyword = validKeywords.find((item) => {
        const text = item.text.trim();
        return text.length > 80 || text.split(/\s+/).length > 10;
      });
      if (invalidKeyword) return { message: '每个关键词最多 80 个字符，且不能超过 10 个单词', field: 'keywords' };
      if (cpcBid.trim() && !toPositiveNumber(cpcBid)) return { message: '默认 CPC 出价必须大于 0', field: 'cpcBid' };
    }

    if (step === 3) {
      if (!adName.trim()) return { message: '广告名称不能为空', field: 'adName' };
      if (!finalURL.trim()) return { message: '最终网址不能为空', field: 'finalURL' };
      if (!isValidURL(finalURL.trim())) return { message: '最终网址必须使用 http 或 https', field: 'finalURL' };
      const normalizedHeadlines = compactTextList(headlines);
      const normalizedDescriptions = compactTextList(descriptions);
      if (normalizedHeadlines.length < 3) return { message: '响应式搜索广告至少需要 3 个标题', field: 'headlines' };
      if (normalizedDescriptions.length < 2) return { message: '响应式搜索广告至少需要 2 个描述', field: 'descriptions' };
      if (normalizedHeadlines.length > 15) return { message: '标题最多 15 个', field: 'headlines' };
      if (normalizedDescriptions.length > 4) return { message: '描述最多 4 个', field: 'descriptions' };
      if (normalizedHeadlines.some((item) => item.length > 30)) return { message: '每个标题最多 30 个字符', field: 'headlines' };
      if (normalizedDescriptions.some((item) => item.length > 90)) return { message: '每个描述最多 90 个字符', field: 'descriptions' };
      if (path1.trim().length > 15) return { message: '路径 1 最多 15 个字符', field: 'path1' };
      if (path2.trim().length > 15) return { message: '路径 2 最多 15 个字符', field: 'path2' };
      if (path2.trim() && !path1.trim()) return { message: '填写路径 2 前必须先填写路径 1', field: 'path1' };
      if (businessName.trim().length > 25) return { message: '商家名称最多 25 个字符', field: 'businessName' };
      if (marketingImages.length > 20) return { message: '营销图片最多 20 张', field: 'marketingImages' };
      if (compactTextList(callouts).some((item) => item.length > 25)) return { message: '每条附加说明最多 25 个字符', field: 'callouts' };
      for (const item of sitelinks) {
        const linkText = item.linkText.trim();
        const final = item.finalURL.trim();
        const d1 = item.description1.trim();
        const d2 = item.description2.trim();
        if (!linkText && !final && !d1 && !d2) continue;
        if (!linkText || !final) return { message: '每个附加链接都需要填写链接文字和最终网址', field: 'sitelinks' };
        if (!isValidURL(final)) return { message: '附加链接最终网址必须使用 http 或 https', field: 'sitelinks' };
        if ((d1 && !d2) || (!d1 && d2)) return { message: '附加链接描述必须同时填写两行', field: 'sitelinks' };
      }
      for (const item of structuredSnippets) {
        const header = item.header.trim();
        const values = parseListInput(item.valuesText);
        if (!header && values.length === 0) continue;
        if (!header || values.length === 0) return { message: '结构化摘要需要填写标题和至少一个值', field: 'structuredSnippets' };
        if (values.length > 10) return { message: '每个结构化摘要最多支持 10 个值', field: 'structuredSnippets' };
      }
      if ((callCountryCode.trim() && !callPhoneNumber.trim()) || (!callCountryCode.trim() && callPhoneNumber.trim())) {
        return { message: '电话资产必须同时填写国家代码和电话号码', field: 'callAsset' };
      }
    }

    return null;
  };

  const onNext = async () => {
    const issue = validateStep();
    if (issue) {
      setError(issue.message);
      scrollToField(issue.field);
      return;
    }
    await saveManualDraftSilently();
    setError(null);
    setStep((current) => Math.min(3, current + 1) as WizardStep);
    scrollFormToTop();
  };

  const onPrev = () => {
    setError(null);
    setStep((current) => Math.max(1, current - 1) as WizardStep);
    scrollFormToTop();
  };

  const onUploadBusinessLogo = async (file: File) => {
    if (!file) return;
    try {
      const dataURL = await fileToDataURL(file);
      setBusinessLogo({ name: file.name, data_url: dataURL });
      setError(null);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : '读取商家 Logo 失败');
    }
  };

  const onUploadMarketingImages = async (files: File[]) => {
    if (files.length === 0) return;
    try {
      const nextImages = await Promise.all(files.map(async (file) => ({
        name: file.name,
        data_url: await fileToDataURL(file),
      })));
      setMarketingImages((current) => [...current, ...nextImages].slice(0, 20));
      setError(null);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : '读取营销图片失败');
    }
  };

  const onSubmit = async () => {
    if (!hasPermission('ad-mgmt:campaigns:create')) {
      setError('暂无创建广告权限');
      return;
    }
    const issue = validateStep();
    if (issue) {
      setError(issue.message);
      scrollToField(issue.field);
      return;
    }
    setError(null);

    setValidating(true);
    try {
      const validation = await accountsApi.validateAdFull(brandId, payload);
      if (!validation.valid) {
        if (validation.campaign_error) {
          setStep(1);
          setError(`广告系列校验失败：${validation.campaign_error}`);
          return;
        }
        if (validation.adset_error) {
          setStep(2);
          setError(`广告组校验失败：${validation.adset_error}`);
          return;
        }
        if (validation.creative_error) {
          setStep(3);
          setError(`广告校验失败：${validation.creative_error}`);
          return;
        }
      }
    } finally {
      setValidating(false);
    }

    setSubmitting(true);
    try {
      await accountsApi.createAdFull(brandId, payload);
      onCreated();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '创建 Google 广告失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAction = useCallback(async () => {
    if (step === 3) {
      const shouldSave = await confirmDialog({
        title: '保存草稿',
        message: '关闭前是否保存当前广告数据为草稿？保存草稿不会校验表单，也不会创建或同步到 Google Ads。',
        confirmText: '保存并关闭',
      });
      if (shouldSave) {
        await saveManualDraftSilently();
      }
      onCancel();
      return;
    }
    await handleCancelWithDraft();
  }, [handleCancelWithDraft, onCancel, saveManualDraftSilently, step]);

  return (
    <div ref={formCardRef}>
      {error && (
        <WizardErrorBanner
          title="Google 参数错误"
          message={error}
          hint="请检查当前步骤的必填项、网络设置和素材限制。"
          bannerRef={errorRef}
        />
      )}

      <StepBar step={step} />

      {step === 1 && (
        <div>
          <SectionCard title="Google 搜索广告系列">
            <FormGroup>
              <Label required>授权账户</Label>
              <div ref={setFieldRef('account')}>
                <MultiSelect
                  single
                  options={accountOptions.map((item) => ({
                    value: item.account_id,
                    label: item.account_id + (item.account_name ? ` (${item.account_name})` : ''),
                  }))}
                  value={selectedAccountID ? [selectedAccountID] : []}
                  onChange={vals => setSelectedAccountID(vals[0] ?? '')}
                  placeholder="请选择 Google 广告账户"
                />
              </div>
            </FormGroup>

            <FormGroup>
              <Label required>广告系列名称</Label>
              <div ref={setFieldRef('campaignName')}>
                <input
                  className="form-input"
                placeholder="例如：US_Search_Glasses_Q2"
                  value={campaignName}
                  onChange={(event) => setCampaignName(event.target.value)}
                />
              </div>
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>广告目标</Label>
                <MultiSelect
                  single
                  options={GOOGLE_OBJECTIVES.map((item) => ({ value: item.value, label: item.label }))}
                  value={campaignObjective ? [campaignObjective] : []}
                  onChange={vals => setCampaignObjective(vals[0] ?? '')}
                />
              </FormGroup>

              <FormGroup>
                <Label>广告系列状态</Label>
                <MultiSelect
                  single
                  options={[
                    { value: 'PAUSED', label: '暂停' },
                    { value: 'ACTIVE', label: '启用' },
                  ]}
                  value={campaignStatus ? [campaignStatus] : []}
                  onChange={vals => setCampaignStatus(vals[0] ?? '')}
                />
              </FormGroup>

              <FormGroup>
                <Label required>日预算</Label>
                <div ref={setFieldRef('dailyBudget')}>
                  <input
                    className="form-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={dailyBudget}
                    onChange={(event) => setDailyBudget(event.target.value)}
                  />
                </div>
                <Hint>将映射到 Google Ads 广告系列预算的 `amount_micros`。</Hint>
              </FormGroup>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>开始时间</Label>
                <DateTimePicker value={startDate} onChange={(value) => setStartDate(toGoogleRFC3339(value))} placeholder="立即开始（留空）" />
                <Hint>支持日期和时间，留空则按创建后立即开始处理。</Hint>
              </FormGroup>
              <FormGroup>
                <Label>结束时间</Label>
                <DateTimePicker value={endDate} onChange={(value) => setEndDate(toGoogleRFC3339(value))} placeholder="持续投放（留空）" />
                <Hint>留空则持续投放，后端会按 Google 可接受格式转换日期。</Hint>
              </FormGroup>
            </div>
          </SectionCard>

          <SectionCard title="国家与语言定向">
            <FormGroup>
              <Label>国家代码</Label>
              <div ref={setFieldRef('countryCodes')}>
                <ToggleChips
                  options={COMMON_COUNTRIES}
                  selected={parseCodeList(countryCodesText)}
                  onToggle={(value) => setCountryCodesText((current) => toggleDelimitedValue(current, value))}
                />
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="例如：US, CA, GB"
                  value={countryCodesText}
                  onChange={(event) => setCountryCodesText(event.target.value)}
                />
              </div>
              <Hint>可通过国家 ISO 代码映射 Google 地域定向，也可以在下方直接填写地域 ID。</Hint>
            </FormGroup>

            <FormGroup>
              <Label>语言代码</Label>
              <div ref={setFieldRef('languageCodes')}>
                <ToggleChips
                  options={COMMON_LANGUAGES}
                  selected={parseLanguageCodeList(languageCodesText)}
                  onToggle={(value) => setLanguageCodesText((current) => toggleDelimitedValue(current, value, false))}
                />
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="例如：en, zh-CN"
                  value={languageCodesText}
                  onChange={(event) => setLanguageCodesText(event.target.value)}
                />
              </div>
              <Hint>Google 语言定向也支持直接填写数字语言常量 ID。</Hint>
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>地域定向（搜索选择）</Label>
                <div ref={setFieldRef('locationIDs')} style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    placeholder="输入地名搜索，如 United States, California..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    onFocus={() => locationResults.length > 0 && setLocationDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setLocationDropdownOpen(false), 200)}
                  />
                  {locationDropdownOpen && locationResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
                      {locationResults.filter((i) => !selectedLocations.some((s) => s.id === i.id)).map((item) => (
                        <div key={item.id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                          onMouseDown={() => { setSelectedLocations((p) => [...p, { id: item.id, name: item.canonical_name || item.name }]); setLocationSearch(''); setLocationResults([]); setLocationDropdownOpen(false); }}
                          onMouseEnter={(e) => { (e.target as HTMLDivElement).style.background = 'var(--primary-bg)'; }}
                          onMouseLeave={(e) => { (e.target as HTMLDivElement).style.background = 'transparent'; }}
                        >{item.canonical_name || item.name} <span style={{ color: 'var(--text3)', fontSize: 12 }}>({item.id})</span></div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedLocations.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {selectedLocations.map((loc) => (
                      <span key={loc.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
                        {loc.name} ({loc.id})
                        <span style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: 2 }} onClick={() => setSelectedLocations((p) => p.filter((i) => i.id !== loc.id))}>x</span>
                      </span>
                    ))}
                  </div>
                )}
                <Hint>搜索并选择 Google Ads 地域定向，可选多个。</Hint>
              </FormGroup>

              <FormGroup>
                <Label>语言定向（搜索选择）</Label>
                <div ref={setFieldRef('languageIDs')} style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    placeholder="输入语言名搜索，如 English, Chinese..."
                    value={languageSearch}
                    onChange={(e) => setLanguageSearch(e.target.value)}
                    onFocus={() => languageResults.length > 0 && setLanguageDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setLanguageDropdownOpen(false), 200)}
                  />
                  {languageDropdownOpen && languageResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
                      {languageResults.filter((i) => !selectedLanguages.some((s) => s.id === i.id)).map((item) => (
                        <div key={item.id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                          onMouseDown={() => { setSelectedLanguages((p) => [...p, { id: item.id, name: item.name }]); setLanguageSearch(''); setLanguageResults([]); setLanguageDropdownOpen(false); }}
                          onMouseEnter={(e) => { (e.target as HTMLDivElement).style.background = 'var(--primary-bg)'; }}
                          onMouseLeave={(e) => { (e.target as HTMLDivElement).style.background = 'transparent'; }}
                        >{item.name} <span style={{ color: 'var(--text3)', fontSize: 12 }}>({item.id})</span></div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedLanguages.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {selectedLanguages.map((lang) => (
                      <span key={lang.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
                        {lang.name} ({lang.id})
                        <span style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: 2 }} onClick={() => setSelectedLanguages((p) => p.filter((i) => i.id !== lang.id))}>x</span>
                      </span>
                    ))}
                  </div>
                )}
                <Hint>搜索并选择 Google Ads 语言定向，可选多个。</Hint>
              </FormGroup>
            </div>
          </SectionCard>

          <SectionCard title="搜索网络设置">
            <div ref={setFieldRef('networks')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={enableGoogleSearch} onChange={(event) => setEnableGoogleSearch(event.target.checked)} />
                Google 搜索
              </label>
              <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={enableSearchNetwork} onChange={(event) => setEnableSearchNetwork(event.target.checked)} />
                搜索网络
              </label>
              <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={enablePartners} onChange={(event) => setEnablePartners(event.target.checked)} />
                搜索合作伙伴网络
              </label>
              <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={enableDisplay} onChange={(event) => setEnableDisplay(event.target.checked)} />
                内容网络
              </label>
            </div>
          </SectionCard>
        </div>
      )}

      {step === 2 && (
        <div>
          <SectionCard title="广告组设置">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label required>广告组名称</Label>
                <div ref={setFieldRef('adsetName')}>
                  <input className="form-input" value={adsetName} onChange={(event) => setAdsetName(event.target.value)} />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>广告组状态</Label>
                <MultiSelect
                  single
                  options={[
                    { value: 'PAUSED', label: '暂停' },
                    { value: 'ACTIVE', label: '启用' },
                  ]}
                  value={adsetStatus ? [adsetStatus] : []}
                  onChange={vals => setAdsetStatus(vals[0] ?? '')}
                />
              </FormGroup>

              <FormGroup>
                <Label>默认 CPC 出价</Label>
                <div ref={setFieldRef('cpcBid')}>
                  <input
                    className="form-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                  placeholder="可选"
                    value={cpcBid}
                    onChange={(event) => setCpcBid(event.target.value)}
                  />
                </div>
                <Hint>将映射到 Google 广告组的 `cpc_bid_micros`。</Hint>
              </FormGroup>
            </div>
          </SectionCard>

          <SectionCard title={`关键词（${keywordCount}）`}>
            <div ref={setFieldRef('keywords')}>
              {keywords.map((item, index) => (
                <div key={`keyword-${index}`} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    placeholder="例如：蓝光眼镜"
                    value={item.text}
                    onChange={(event) => {
                      const next = [...keywords];
                      next[index] = { ...next[index], text: event.target.value };
                      setKeywords(next);
                    }}
                  />
                  <MultiSelect
                    single
                    style={{ width: 120 }}
                    options={MATCH_TYPES.map((matchType) => ({ value: matchType, label: MATCH_TYPE_LABELS[matchType] }))}
                    value={item.matchType ? [item.matchType] : []}
                    onChange={vals => {
                      const next = [...keywords];
                      next[index] = { ...next[index], matchType: (vals[0] ?? '') as KeywordMatchType };
                      setKeywords(next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={keywords.length <= 1}
                    onClick={() => setKeywords((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>

            <button type="button" className="btn btn-outline btn-sm" onClick={() => setKeywords((current) => [...current, emptyKeyword()])}>
              添加关键词
            </button>
            <Hint>支持广泛、词组、完全匹配，Google Ads 的关键词限制会由后端再次校验。</Hint>
          </SectionCard>
        </div>
      )}

      {step === 3 && (
        <div>
          <SectionCard title="响应式搜索广告">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label required>广告名称</Label>
                <div ref={setFieldRef('adName')}>
                  <input className="form-input" value={adName} onChange={(event) => setAdName(event.target.value)} />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>广告状态</Label>
                <MultiSelect
                  single
                  options={[
                    { value: 'PAUSED', label: '暂停' },
                    { value: 'ACTIVE', label: '启用' },
                  ]}
                  value={adStatus ? [adStatus] : []}
                  onChange={vals => setAdStatus(vals[0] ?? '')}
                />
              </FormGroup>
            </div>

            <FormGroup>
              <Label required>最终网址</Label>
              <div ref={setFieldRef('finalURL')}>
                <input
                  className="form-input"
                placeholder="https://www.example.com"
                  value={finalURL}
                  onChange={(event) => setFinalURL(event.target.value)}
                />
              </div>
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>路径 1</Label>
                <div ref={setFieldRef('path1')}>
                  <input className="form-input" maxLength={15} value={path1} onChange={(event) => setPath1(event.target.value)} />
                </div>
              </FormGroup>
              <FormGroup>
                <Label>路径 2</Label>
                <div ref={setFieldRef('path2')}>
                  <input className="form-input" maxLength={15} value={path2} onChange={(event) => setPath2(event.target.value)} />
                </div>
              </FormGroup>
            </div>
          </SectionCard>

          <SectionCard title={`标题（${compactTextList(headlines).length}/15）`}>
            <div ref={setFieldRef('headlines')}>
              {headlines.map((value, index) => (
                <div key={`headline-${index}`} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    maxLength={30}
                    placeholder={`标题 ${index + 1}`}
                    value={value}
                    onChange={(event) => {
                      const next = [...headlines];
                      next[index] = event.target.value;
                      setHeadlines(next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={headlines.length <= 3}
                    onClick={() => setHeadlines((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>
            {headlines.length < 15 && (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setHeadlines((current) => [...current, ''])}>
                添加标题
              </button>
            )}
          </SectionCard>

          <SectionCard title={`描述（${compactTextList(descriptions).length}/4）`}>
            <div ref={setFieldRef('descriptions')}>
              {descriptions.map((value, index) => (
                <div key={`description-${index}`} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input
                    className="form-input"
                    style={{ flex: 1 }}
                    maxLength={90}
                    placeholder={`描述 ${index + 1}`}
                    value={value}
                    onChange={(event) => {
                      const next = [...descriptions];
                      next[index] = event.target.value;
                      setDescriptions(next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={descriptions.length <= 2}
                    onClick={() => setDescriptions((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>
            {descriptions.length < 4 && (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setDescriptions((current) => [...current, ''])}>
                添加描述
              </button>
            )}
          </SectionCard>

          <SectionCard title="广告系列资产">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>商家名称</Label>
                <div ref={setFieldRef('businessName')}>
                  <input
                    className="form-input"
                    maxLength={25}
                    placeholder="品牌名称"
                    value={businessName}
                    onChange={(event) => setBusinessName(event.target.value)}
                  />
                </div>
                <Hint>用于 Google 广告系列的商家名称资产。</Hint>
              </FormGroup>

              <FormGroup>
                <Label>商家 Logo</Label>
                <UploadZone
                  onFile={onUploadBusinessLogo}
                  accept="image/*"
                  hint="JPG / PNG，建议使用方形 Logo，主体清晰"
                  fileName={businessLogo?.name}
                  previewURL={businessLogo?.data_url}
                />
                {businessLogo && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)' }}>
                    {businessLogo.name}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBusinessLogo(null)} style={{ marginLeft: 8 }}>
                      删除
                    </button>
                  </div>
                )}
              </FormGroup>
            </div>

            <FormGroup>
              <Label>营销图片</Label>
              <div ref={setFieldRef('marketingImages')}>
                <UploadZone
                  onFiles={onUploadMarketingImages}
                  accept="image/*"
                  multiple
                  hint="可一次选择多张 JPG / PNG，建议突出品牌与利益点"
                  fileName={marketingImages.length > 0 ? `已选择 ${marketingImages.length} 张图片` : undefined}
                />
              </div>
              <Hint>可选，挂载在广告系列级别的图片资产，最多 20 张。</Hint>
              {marketingImages.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {marketingImages.map((item, index) => (
                    <div key={`${item.name}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>{item.name}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setMarketingImages((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        删除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>
          </SectionCard>

          <SectionCard title="附加信息">
            <FormGroup>
              <Label>附加说明</Label>
              <div ref={setFieldRef('callouts')}>
                {callouts.map((value, index) => (
                  <div key={`callout-${index}`} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input
                      className="form-input"
                      style={{ flex: 1 }}
                      maxLength={25}
                        placeholder={`附加说明 ${index + 1}`}
                      value={value}
                      onChange={(event) => {
                        const next = [...callouts];
                        next[index] = event.target.value;
                        setCallouts(next);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={callouts.length <= 1}
                      onClick={() => setCallouts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      删除
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setCallouts((current) => [...current, ''])}>
                添加附加说明
              </button>
            </FormGroup>

            <FormGroup>
              <Label>附加链接</Label>
              <div ref={setFieldRef('sitelinks')}>
                {sitelinks.map((item, index) => (
                  <div key={`sitelink-${index}`} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      <input
                        className="form-input"
                        placeholder="链接文字"
                        value={item.linkText}
                        onChange={(event) => {
                          const next = [...sitelinks];
                          next[index] = { ...next[index], linkText: event.target.value };
                          setSitelinks(next);
                        }}
                      />
                      <input
                        className="form-input"
                        placeholder="https://www.example.com/page"
                        value={item.finalURL}
                        onChange={(event) => {
                          const next = [...sitelinks];
                          next[index] = { ...next[index], finalURL: event.target.value };
                          setSitelinks(next);
                        }}
                      />
                      <input
                        className="form-input"
                        placeholder="描述第 1 行"
                        value={item.description1}
                        onChange={(event) => {
                          const next = [...sitelinks];
                          next[index] = { ...next[index], description1: event.target.value };
                          setSitelinks(next);
                        }}
                      />
                      <input
                        className="form-input"
                        placeholder="描述第 2 行"
                        value={item.description2}
                        onChange={(event) => {
                          const next = [...sitelinks];
                          next[index] = { ...next[index], description2: event.target.value };
                          setSitelinks(next);
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={sitelinks.length <= 1}
                        onClick={() => setSitelinks((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        删除附加链接
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setSitelinks((current) => [...current, emptySitelink()])}>
                添加附加链接
              </button>
            </FormGroup>

            <FormGroup>
              <Label>结构化摘要</Label>
              <div ref={setFieldRef('structuredSnippets')}>
                {structuredSnippets.map((item, index) => (
                  <div key={`snippet-${index}`} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      <MultiSelect
                        single
                        options={snippetHeaderOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
                        value={item.header ? [item.header] : []}
                        onChange={vals => {
                          const next = [...structuredSnippets];
                          next[index] = { ...next[index], header: vals[0] ?? '' };
                          setStructuredSnippets(next);
                        }}
                        placeholder="-- 选择标题 --"
                      />
                      <input
                        className="form-input"
                        placeholder="值 1, 值 2, 值 3"
                        value={item.valuesText}
                        onChange={(event) => {
                          const next = [...structuredSnippets];
                          next[index] = { ...next[index], valuesText: event.target.value };
                          setStructuredSnippets(next);
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={structuredSnippets.length <= 1}
                        onClick={() => setStructuredSnippets((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        删除摘要
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setStructuredSnippets((current) => [...current, emptySnippet()])}
              >
                添加结构化摘要
              </button>
            </FormGroup>

            <div ref={setFieldRef('callAsset')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label>电话资产国家代码</Label>
                <input
                  className="form-input"
                  placeholder="US"
                  value={callCountryCode}
                  onChange={(event) => setCallCountryCode(event.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>电话资产号码</Label>
                <input
                  className="form-input"
                  placeholder="+1 800 555 1234"
                  value={callPhoneNumber}
                  onChange={(event) => setCallPhoneNumber(event.target.value)}
                />
              </FormGroup>
            </div>
          </SectionCard>

          <SectionCard title={`促销扩展（${promotions.length}）`}>
            <Hint>可选。为广告添加促销信息（折扣、优惠码等）。</Hint>
            {promotions.map((promo, idx) => (
              <div key={`promo-${idx}`} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginTop: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>促销 {idx + 1}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPromotions((p) => p.filter((_, i) => i !== idx))}>删除</button>
                </div>
                <FormGroup>
                  <Label required>促销目标描述</Label>
                  <input className="form-input" placeholder="例：Free shipping、20% off all items" value={promo.promotionTarget} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, promotionTarget: e.target.value } : item))} />
                  <Hint>必填。描述促销内容，如"全场包邮"、"新用户首单立减"。</Hint>
                </FormGroup>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  <FormGroup>
                    <Label>促销时机</Label>
                    <MultiSelect
                      single
                      options={PROMOTION_OCCASIONS.map((o) => ({ value: o.value, label: o.label }))}
                      value={promo.occasion ? [promo.occasion] : []}
                      onChange={vals => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, occasion: vals[0] ?? '' } : item))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>折扣方式</Label>
                    <MultiSelect
                      single
                      options={[
                        { value: 'NONE', label: '精确折扣' },
                        { value: 'UP_TO', label: '最高可达' },
                      ]}
                      value={promo.discountModifier ? [promo.discountModifier] : []}
                      onChange={vals => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, discountModifier: vals[0] ?? '' } : item))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>百分比折扣</Label>
                    <input className="form-input" type="number" min="0" max="100" placeholder="例：20（%）" value={promo.percentOff} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, percentOff: e.target.value, moneyAmountOff: '' } : item))} disabled={!!promo.moneyAmountOff} />
                    <Hint>与金额折扣互斥。</Hint>
                  </FormGroup>
                  <FormGroup>
                    <Label>金额折扣</Label>
                    <input className="form-input" type="number" min="0" step="0.01" placeholder="例：10.00" value={promo.moneyAmountOff} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, moneyAmountOff: e.target.value, percentOff: '' } : item))} disabled={!!promo.percentOff} />
                  </FormGroup>
                  <FormGroup>
                    <Label>币种</Label>
                    <input className="form-input" placeholder="USD" value={promo.currencyCode} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, currencyCode: e.target.value } : item))} />
                  </FormGroup>
                  <FormGroup>
                    <Label>优惠码</Label>
                    <input className="form-input" placeholder="与满减金额二选一" value={promo.promotionCode} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, promotionCode: e.target.value, ordersOverAmount: '' } : item))} disabled={!!promo.ordersOverAmount} />
                  </FormGroup>
                  <FormGroup>
                    <Label>满减金额</Label>
                    <input className="form-input" type="number" min="0" step="0.01" placeholder="与优惠码二选一" value={promo.ordersOverAmount} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, ordersOverAmount: e.target.value, promotionCode: '' } : item))} disabled={!!promo.promotionCode} />
                    <Hint>优惠码与满减金额可选填其一。</Hint>
                  </FormGroup>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 8 }}>
                  <FormGroup>
                    <Label required>落地页 URL</Label>
                    <input className="form-input" placeholder="https://..." value={promo.finalURL} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, finalURL: e.target.value } : item))} />
                  </FormGroup>
                  <FormGroup>
                    <Label>开始日期</Label>
                    <input className="form-input" type="date" value={promo.startDate} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, startDate: e.target.value } : item))} />
                  </FormGroup>
                  <FormGroup>
                    <Label>结束日期</Label>
                    <input className="form-input" type="date" value={promo.endDate} onChange={(e) => setPromotions((p) => p.map((item, i) => i === idx ? { ...item, endDate: e.target.value } : item))} />
                  </FormGroup>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 4 }} onClick={() => setPromotions((p) => [...p, emptyPromotion()])}>+ 添加促销扩展</button>
          </SectionCard>

          <SectionCard title={`价格扩展（${priceAssets.length}）`}>
            <Hint>可选。展示产品/服务的价格列表（每组至少 3 项，最多 8 项）。</Hint>
            {priceAssets.map((pa, paIdx) => (
              <div key={`price-${paIdx}`} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginTop: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>价格组 {paIdx + 1}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPriceAssets((p) => p.filter((_, i) => i !== paIdx))}>删除</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 8 }}>
                  <FormGroup>
                    <Label required>价格类型</Label>
                    <MultiSelect
                      single
                      options={PRICE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                      value={pa.priceType ? [pa.priceType] : []}
                      onChange={vals => setPriceAssets((p) => p.map((item, i) => i === paIdx ? { ...item, priceType: vals[0] ?? '' } : item))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>语言</Label>
                    <input className="form-input" placeholder="en" value={pa.languageCode} onChange={(e) => setPriceAssets((p) => p.map((item, i) => i === paIdx ? { ...item, languageCode: e.target.value } : item))} />
                  </FormGroup>
                </div>
                {pa.items.map((item, itemIdx) => (
                  <div key={`price-${paIdx}-${itemIdx}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
                    <FormGroup>
                      <Label required>标题</Label>
                      <input className="form-input" maxLength={25} placeholder="最多 25 字符" value={item.header} onChange={(e) => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.map((it, ii) => ii === itemIdx ? { ...it, header: e.target.value } : it) } : a))} />
                    </FormGroup>
                    <FormGroup>
                      <Label>描述</Label>
                      <input className="form-input" maxLength={25} placeholder="最多 25 字符" value={item.description} onChange={(e) => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.map((it, ii) => ii === itemIdx ? { ...it, description: e.target.value } : it) } : a))} />
                    </FormGroup>
                    <FormGroup>
                      <Label required>价格</Label>
                      <input className="form-input" type="number" min="0" step="0.01" value={item.price} onChange={(e) => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.map((it, ii) => ii === itemIdx ? { ...it, price: e.target.value } : it) } : a))} />
                    </FormGroup>
                    <FormGroup>
                      <Label>单位</Label>
                      <MultiSelect
                        single
                        options={PRICE_UNITS.map((u) => ({ value: u.value, label: u.label }))}
                        value={item.unit ? [item.unit] : []}
                        onChange={vals => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.map((it, ii) => ii === itemIdx ? { ...it, unit: vals[0] ?? '' } : it) } : a))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label required>链接</Label>
                      <input className="form-input" placeholder="https://..." value={item.finalURL} onChange={(e) => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.map((it, ii) => ii === itemIdx ? { ...it, finalURL: e.target.value } : it) } : a))} />
                    </FormGroup>
                    <FormGroup style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: a.items.filter((_, ii) => ii !== itemIdx) } : a))} disabled={pa.items.length <= 3}>删除</button>
                    </FormGroup>
                  </div>
                ))}
                {pa.items.length < 8 && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setPriceAssets((p) => p.map((a, ai) => ai === paIdx ? { ...a, items: [...a.items, emptyPriceItem()] } : a))}>+ 添加价格项</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 4 }} onClick={() => setPriceAssets((p) => [...p, emptyPriceAsset()])}>+ 添加价格扩展</button>
          </SectionCard>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        <button className="btn btn-ghost" onClick={onPrev} disabled={step === 1 || submitting || validating}>
          上一步
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => { void handleCloseAction(); }} disabled={submitting || validating}>
            关闭
          </button>
          <button className="btn btn-outline" onClick={() => { void handleResetDraft(); }} disabled={submitting || validating}>
            重置内容
          </button>
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => { void onNext(); }} disabled={submitting || validating}>
              下一步
            </button>
          ) : (
            <button className="btn btn-primary" onClick={onSubmit} disabled={submitting || validating}>
              {validating ? '校验中...' : submitting ? '创建中...' : '创建 Google 广告'}
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
